import { useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';
import { createEmbeddedImage } from '../utils/createEmbeddedImage';

/**
 * Hook that listens for paste events and adds images to the scene
 * Supports Ctrl+V to paste images from clipboard
 *
 * @param {THREE.Scene} scene - Three.js scene
 * @param {THREE.Camera} camera - Three.js camera
 * @param {THREE.WebGLRenderer} renderer - Three.js renderer
 * @param {Object} options - Additional options
 * @param {string} options.layerName - Default layer name for pasted images
 * @param {number} options.defaultWidth - Default width for images (in scene units)
 */
export function useImagePaste(scene, camera, renderer, options = {}) {
  const { layerName = '0', defaultWidth = 50 } = options;

  // Track last mouse position in world coordinates
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  // Convert screen coordinates to world coordinates
  const screenToWorld = useCallback((clientX, clientY) => {
    if (!renderer || !camera) return { x: 0, y: 0 };

    const rect = renderer.domElement.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;

    const vector = new THREE.Vector3(ndcX, ndcY, 0);
    vector.unproject(camera);

    return { x: vector.x, y: vector.y };
  }, [camera, renderer]);

  // Track mouse movement to know paste location
  useEffect(() => {
    if (!renderer) return;

    const handleMouseMove = (e) => {
      const worldPos = screenToWorld(e.clientX, e.clientY);
      lastMousePosRef.current = worldPos;
    };

    const canvas = renderer.domElement;
    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [renderer, screenToWorld]);

  // Handle paste event
  const handlePaste = useCallback(async (event) => {
    if (!scene) return;

    // Check if there are image items in clipboard
    const items = event.clipboardData?.items;
    if (!items) return;

    let imageFile = null;

    // Find image in clipboard items
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        imageFile = item.getAsFile();
        break;
      }
    }

    if (!imageFile) {
      // No image in clipboard - let other handlers process
      return;
    }

    // Prevent default paste behavior
    event.preventDefault();

    try {
      // Read file as data URL
      const reader = new FileReader();

      reader.onload = async (e) => {
        const dataUrl = e.target.result;

        // Use last mouse position for placement
        const mousePos = lastMousePosRef.current;

        // Create embedded image at mouse position
        const imageMesh = await createEmbeddedImage({
          src: dataUrl,
          x: mousePos.x - defaultWidth / 2,
          y: mousePos.y - defaultWidth / 2,
          width: defaultWidth,
          layerName,
          name: `pasted_image_${Date.now()}`
        });

        // Add to scene
        scene.add(imageMesh);

        // Dispatch event for undo/redo tracking
        window.dispatchEvent(new CustomEvent('imagePasted', {
          detail: { mesh: imageMesh }
        }));
      };

      reader.onerror = () => {
        console.error('Failed to read pasted image');
      };

      reader.readAsDataURL(imageFile);

    } catch (error) {
      console.error('Error pasting image:', error);
    }
  }, [scene, defaultWidth, layerName]);

  // Set up paste event listener
  useEffect(() => {
    if (!scene) return;

    // Add paste event listener to document
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [scene, handlePaste]);

  return {
    handlePaste,
    lastMousePos: lastMousePosRef.current
  };
}

export default useImagePaste;
